import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { advanceToNextTrackWordlist } from 'src/apps/VocabTracks/advanceToNextTrackWordlist';
import { useEligibility } from 'src/hooks/useEligibility';

/**
 * Moves a vocab-track learner on to the next word list as soon as they finish the
 * last lesson of the current one, so Home and Path already show the next list
 * instead of a finished one that Learn cannot start.
 */
export const useAdvanceTrackWordlistOnComplete = (
  languagePairId: number | null,
  enabled: boolean,
): void => {
  const queryClient = useQueryClient();
  const { isEligibleForTracksAndThemes, isLoading: isEligibilityLoading } =
    useEligibility(languagePairId);
  // Keyed by language pair so a course switch is still handled, while repeat
  // renders (and React's development double-invoke) advance only once.
  const advancedLanguagePairId = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || isEligibilityLoading || !isEligibleForTracksAndThemes || !languagePairId)
      return;
    if (advancedLanguagePairId.current === languagePairId) return;

    advancedLanguagePairId.current = languagePairId;
    // Failures are silent: Home advances again when the learner starts their next
    // lesson, so there is nothing useful to tell them here.
    void advanceToNextTrackWordlist(languagePairId, queryClient).catch(() => {});
  }, [enabled, isEligibilityLoading, isEligibleForTracksAndThemes, languagePairId, queryClient]);
};
